import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import OrdersLargeContainer from '@/components/front-office/containers/OrdersLargeContainer';
import { useOrdersContext } from '@/contexts/OrderContext';
import Cart from '@/components/front-office/orders/Cart';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import PictureDiv from '@/components/front-office/orders/PictureDiv';
import { useLocation } from 'react-router-dom';
import MinimumPrice from '@/components/front-office/orders/MinimumPrice';

const OPTIONS_LIMIT = 50;

const NoLimitListProductSelection = () => {
    const { setCart, cart, saveCart } = useOrdersContext();
    const [products, setProducts] = useState([]);
    const [initialTotalQuantity, setInitialTotalQuantity] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [isInMenu, setIsInMenu] = useState(false);
    const [menuItem, setMenuItem] = useState({});
    const [canSaveToIDB, setCanSaveToIDB] = useState(false);
    const [canLoadMoreOptions, setCanLoadMoreOptions] = useState(false);
    const [lastDocId, setLastDocId] = useState(null);
    const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
    const location = useLocation();
    const loadedCart = location.state || cart;
    const observer = useRef();

    const fetchInitialProducts = async () => {
        try {
            if (loadedCart?.restaurant_id) {
                // Récupérer la liste des catégories triées (uniquement les catégories visibles)
                const categoriesResponse = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/settings/categories/list`
                );

                // Trier les catégories par priorité
                const sortedCategories = categoriesResponse.data.sort((a, b) => a.priority - b.priority);

                // Charger les produits de toutes les catégories en parallèle
                const productsPromises = sortedCategories.map(async (category) => {
                    const response = await axios.get(
                        `${import.meta.env.VITE_API_URL}/api/restaurants/${loadedCart?.restaurant_id}/no-limit/${category.id}/products/list`
                    );
                    return {
                        category_name: category.name,
                        products: response.data,
                        priority: category.priority
                    };
                });

                const productsResults = await Promise.all(productsPromises);
                // Trier les produits par ordre alphabétique dans chaque catégorie
                const sortedProductsResults = productsResults.map(category => ({
                    ...category,
                    products: category.products.sort((a, b) => a.name.localeCompare(b.name))
                }));
                setProducts(sortedProductsResults);

                const currentMenuItem = cart.categories.sort((a, b) => a.priority - b.priority)[0];
                setMenuItem(currentMenuItem);
                setInitialTotalQuantity(0);
                setTotalQuantity(0);
            }
        } catch (e) {
            console.info(e);
            toast.info('Une erreur est survenue', {
                position: 'top-center',
                autoClose: 500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        if (canLoadMoreOptions) {
            setCanLoadMoreOptions(false); // Désactiver le chargement infini car nous chargeons tout d'un coup
        }
    }, [canLoadMoreOptions]);

    // Effect to fetch products when the menu changes or current step changes
    useEffect(() => {
        fetchInitialProducts();
    }, [loadedCart?.restaurant_id]);

    // Effect to handle total quantity and selected products
    useEffect(() => {
        const category = cart.categories.find((category) => category?.id === menuItem?.id);
        if (category) {
            const totalSelectedProducts = category.extra_products?.reduce((sum, item) => sum + item.selected_quantity, 0);
            setTotalQuantity(initialTotalQuantity - totalSelectedProducts); // Update total quantity left
        }

        setCanSaveToIDB(true);
    }, [cart, menuItem, loadedCart]);

    useEffect(() => {
        if (canSaveToIDB) {
            const saveCartInIndexedDB = async () => {
                await saveCart(cart.restaurant_id, cart);
            };

            saveCartInIndexedDB();
            setCanSaveToIDB(false);
        }
    }, [canSaveToIDB]);

    const handleAddProduct = (product) => {
        setCart((prevCart) => {
            return {
                ...prevCart,
                categories: prevCart.categories.map((category) => {
                    if (category.id === product.category_id) {
                        if (!isInMenu) {
                            const existingProductIndex = category.extra_products?.findIndex((p) => p.product_id === product.product_id);

                            let updatedExtraProducts;

                            if (existingProductIndex !== -1) {
                                updatedExtraProducts = category.extra_products?.map((p, index) =>
                                    index === existingProductIndex ? { ...p, selected_quantity: p.selected_quantity + 1 } : p
                                );
                            } else {
                                updatedExtraProducts = [...category.extra_products, { ...product, selected_quantity: 1 }];
                            }

                            return {
                                ...category,
                                extra_products: updatedExtraProducts,
                            };
                        }
                    }

                    return category;
                }),
            };
        });

        if (totalQuantity > 0) {
            setTotalQuantity((prevTotal) => prevTotal - 1);
        }

        setCanSaveToIDB(true);
    };

    const handleRemoveProduct = (product) => {
        setCart((prevCart) => {
            return {
                ...prevCart,
                categories: prevCart.categories.map((category) => {
                    if (category.id === product.category_id) {
                        const productInSelectedProducts = category.selected_products?.find((p) => p.product_id === product.product_id);
                        const productInExtraProducts = category.extra_products?.find((p) => p.product_id === product.product_id);

                        let updatedSelectedProducts = category.selected_products;
                        let updatedExtraProducts = category.extra_products;

                        if (productInSelectedProducts) {
                            const indexSelected = category.selected_products.findIndex((p) => p.product_id === product.product_id);
                            if (indexSelected !== -1) {
                                updatedSelectedProducts = [...category.selected_products];
                                updatedSelectedProducts[indexSelected].selected_quantity -= 1;

                                if (updatedSelectedProducts[indexSelected].selected_quantity <= 0) {
                                    updatedSelectedProducts.splice(indexSelected, 1);
                                }
                            }
                        }

                        if (productInExtraProducts) {
                            const indexExtra = category.extra_products.findIndex((p) => p.product_id === product.product_id);
                            const extraProduct = category.extra_products[indexExtra];

                            // CHECK THAT THE PRODUCT TO REMOVE IS IN THE SELECTED MENUS, IF SO, THEN LEAVE THE CODE AS IS
                            const isInAnyOfTheSelectedMenus =
                                cart.menus.filter((menu) => menu.steps?.some((step) => step.category_id === extraProduct.id)).length >
                                0;

                            if (
                                (isInAnyOfTheSelectedMenus &&
                                    indexExtra !== -1 &&
                                    extraProduct.selected_quantity - 1 > initialTotalQuantity) ||
                                (!isInAnyOfTheSelectedMenus && !productInSelectedProducts)
                            ) {
                                updatedExtraProducts = [...category.extra_products];
                                updatedExtraProducts[indexExtra].selected_quantity -= 1;
                            }

                            if (updatedExtraProducts[indexExtra].selected_quantity <= 0) {
                                updatedExtraProducts.splice(indexExtra, 1);
                            }
                        }

                        const totalSelectedProducts = updatedSelectedProducts.reduce(
                            (sum, item) => sum + item.selected_quantity,
                            0
                        );

                        if (totalSelectedProducts < initialTotalQuantity) {
                            let remainingQuantityNeeded = initialTotalQuantity - totalSelectedProducts;

                            updatedExtraProducts = updatedExtraProducts
                                .map((product) => {
                                    if (remainingQuantityNeeded > 0) {
                                        const transferQuantity = Math.min(product.selected_quantity, remainingQuantityNeeded);

                                        if (transferQuantity > 0) {
                                            const existingIndex = updatedSelectedProducts.findIndex((p) => p.product_id === product.product_id);
                                            if (existingIndex !== -1) {
                                                updatedSelectedProducts[existingIndex].selected_quantity += transferQuantity;
                                            } else {
                                                updatedSelectedProducts.push({
                                                    ...product,
                                                    selected_quantity: transferQuantity,
                                                });
                                            }

                                            product.selected_quantity -= transferQuantity;
                                            remainingQuantityNeeded -= transferQuantity;
                                        }
                                    }
                                    return product;
                                })
                                .filter((p) => p.selected_quantity > 0);
                        }

                        const isDoneCondition =
                            category.is_done && updatedSelectedProducts.length === 0 && updatedExtraProducts.length === 0;

                        return {
                            ...category,
                            selected_products: updatedSelectedProducts,
                            extra_products: updatedExtraProducts,
                            is_done: isDoneCondition,
                        };
                    }
                    return category;
                }),
            };
        });

        setTotalQuantity((prevTotal) => Math.min(prevTotal + 1, initialTotalQuantity));
        setCanSaveToIDB(true);
    };

    const handleGetTotalProductAmount = (product) => {
        const selectedProduct = cart.categories
            .find((category) => category?.selected_products?.find((item) => item.product_id === product.product_id))
            ?.selected_products?.find((item) => item.product_id === product.product_id);
        const extraProduct = cart.categories
            .find((category) => category?.extra_products?.find((item) => item.product_id === product.product_id))
            ?.extra_products?.find((item) => item.product_id === product.product_id);

        let result = 0;
        if (selectedProduct) {
            result += selectedProduct?.selected_quantity ?? 0;
        }
        if (extraProduct) {
            result += extraProduct?.selected_quantity ?? 0;
        }

        return result;
    };

    const handleGetProductQuantity = (product) => {
        return product.quantity - handleGetTotalProductAmount(product);
    };

    const handleResetValues = async () => {
        setTotalQuantity(0);
        setInitialTotalQuantity(0);
    };

    const lastProductObserver = useCallback((node) => {
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setCanLoadMoreOptions(true);
            }
        });

        if (node) observer.current.observe(node);
    }, []);

    return (
        <>
            <OrdersLargeContainer isInStepper={false}>
                <div className="col-span-3 bg-white/90 rounded-3xl shadow-2xl p-4 text-center lg:col-span-2">
                    <div className="overflow-hidden border-b-2 border-black/.90 pb-2">
                        <h2 className="font-bold scroll-text text-lg md:text-2xl xl:text-3xl">
                            Composez votre panier, puis cliquez sur Payer
                        </h2>
                    </div>

                    <div className='my-4'>
                        <MinimumPrice />
                    </div>

                    <div
                        id="sidebar-content"
                        className={`p-2 overflow-x-hidden ${window.innerHeight > 568 ? 'h-[calc(100dvh-380px)]' : 'h-[calc(100vh-400px)]'} md:landscape:h-[calc(100vh-350px)] md:h-[calc(100vh-500px)] md:items-center lg:h-[calc(100vh-400px)] md:landscape:gap-12 ${window.innerHeight <= 800 && window.innerWidth <= 1440 ? 'xl:grid-cols-2' : 'xl:grid-cols-3'}`}
                    >
                        {products.length > 0 &&
                            <div className='grid gap-4'>
                                {
                                    products.map((product, index) => (
                                        product.products.length > 0 && (
                                            <div key={product?.category_name || index} className='border-b border-black/10 pb-4'>
                                                <h3 className="ml-2 mb-2 text-lg text-center font-bold text-gray-700 lg:text-left">
                                                    {product.category_name}
                                                </h3>
                                                <div className="grid grid-cols-2 gap-2 md:landscape:gap-8 md:landscape:grid-cols-2 md:grid-cols-3 xl:landscape:grid-cols-3">
                                                    {
                                                        product.products.map((product) => (
                                                            <div ref={index === products.length - 1 ? lastProductObserver : null} key={product?.id} className="mb-4 flex flex-col items-center justify-between lg:items-center lg:justify-start lg:mb-0 lg:flex-row">
                                                                <div className="relative mx-auto">
                                                                    {cart.categories.find(
                                                                        (category) =>
                                                                            category?.selected_products?.find((item) => item.product_id === product.product_id) ||
                                                                            category?.extra_products?.find((item) => item.product_id === product.product_id)
                                                                    ) && (
                                                                            <div className="text-white absolute top-0 left-0 -translate-x-1/3 -translate-y-1/3 w-6 h-6 bg-primary rounded-2xl z-10 text-xs flex justify-center items-center">
                                                                                {handleGetTotalProductAmount(product)}
                                                                            </div>
                                                                        )}

                                                                    <PictureDiv path={product.picture} name={product.name} alt={product.name} />

                                                                    <div
                                                                        className={`absolute top-0 right-0 bottom-0 left-0 w-full h-full ${cart.categories.find(
                                                                            (category) =>
                                                                                category?.selected_products?.find((item) => item.product_id === product.product_id) ||
                                                                                category?.extra_products?.find((item) => item.product_id === product.product_id)
                                                                        )
                                                                            ? 'bg-black/50'
                                                                            : 'opacity-0 hover:opacity-100'
                                                                            } transition duration-300 ease-in-out bg-black/50 rounded-2xl`}
                                                                    >
                                                                        <div className="h-full text-white flex justify-center items-center gap-4">
                                                                            <button
                                                                                className="bg-primary rounded-full w-6 h-6"
                                                                                onClick={() => handleRemoveProduct(product)}
                                                                            >
                                                                                -
                                                                            </button>
                                                                            <p>{handleGetTotalProductAmount(product)}</p>
                                                                            <button
                                                                                className={`rounded-full w-6 h-6 ${handleGetProductQuantity(product) === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary'}`}
                                                                                onClick={() => handleAddProduct(product)}
                                                                                disabled={handleGetProductQuantity(product) === 0}
                                                                            >
                                                                                +
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="text-primary leading-none grid grid-cols-1 text-center lg:ml-4 lg:text-left">
                                                                    <h2 className="text-md leading-none font-bold self-center w-[120px] mb-2">{product.name}</h2>

                                                                    {handleGetProductQuantity(product) <= 10 && <small className={'text-red-500 font-bold'}>{handleGetProductQuantity(product) === 0 ? 'Victime de son succès' : `Il reste : ${handleGetProductQuantity(product)}`}</small>}

                                                                    <Button
                                                                        disabled={handleGetProductQuantity(product) > 0}
                                                                        action={null}
                                                                        handleClick={() => handleAddProduct(product)}
                                                                        text={`${product.price}€`}
                                                                        extraClasses="mt-2 min-w-[80px] self-end"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        )
                                    ))
                                }
                            </div>
                        }
                    </div>

                    <div className='lg:hidden'>
                        <Cart
                            buttonText='Commander'
                            buttonRoute={'/commande/paiement'}
                            cart={cart}
                            handleResetValues={handleResetValues}
                            isStepper={true}
                            showOnlyButton={true}
                            isNoLimit={true}
                            hasMinimumAmount={true}
                        />
                    </div>
                </div>

                <div className="hidden lg:block">
                    <Cart
                        buttonText='Commander'
                        buttonRoute={'/commande/paiement'}
                        cart={cart}
                        handleResetValues={handleResetValues}
                        isStepper={true}
                        isNoLimit={true}
                        hasMinimumAmount={true}
                    />
                </div>
            </OrdersLargeContainer>
        </>
    );
};

export default NoLimitListProductSelection;